// SPDX-License-Identifier: LicenseRef-Techsafe-Proprietary
// Copyright (c) 2026 Techsafe Teknoloji ve Muhendislik A.S.
// https://techsafe-solutions.com
// Economic rights: Techsafe A.S.
// Authors' moral rights reserved under Turkish Copyright Law (FSEK 5846).
// Mali haklar Techsafe A.S.'ye aittir; eser sahiplerinin manevi haklari saklidir (FSEK 5846).
// This content was prepared with the assistance of AI tools, reviewed by a
// human, and published by Techsafe Teknoloji ve Muhendislik A.S. The publishing
// entity is responsible for accuracy and compliance.
// Bu icerik, yapay zeka destekli araclar kullanilarak hazirlanmis, insan
// incelemesinden gecirilmis ve Techsafe Teknoloji ve Muhendislik A.S.
// tarafindan yayimlanmistir. Icerigin dogrulugundan yayimlayan kurulus
// sorumludur.
//
// One display format for every recorded timestamp in this widget:
// "07-Aug-2026 12:06:37" (DD-Mon-YYYY HH:mm:ss). Display contracts carry
// timestamps as plain ISO 8601 strings (see contracts/display.ts), produced
// by the bridge mappers — notably au0001-response-mapper.ts's
// `toIsoFromZohoDate`, which appends "Z" to the recorded Zoho
// `DD-MMM-YYYY HH:mm:ss` digits WITHOUT any real timezone conversion (Zoho
// Creator's own app-level Time Zone setting is the single source of truth for
// what those digits mean — this widget never re-interprets them).
//
// That "Z" is therefore NOT a claim that the value is UTC — it is only there
// to make the string `Date.parse`-safe for the D3 selection compare in
// orchestrator.ts. Formatting for display must NOT go through a `Date`
// object at all: `new Date(...).getUTC*()` happens to reproduce the original
// digits today (parsing a "...Z" string yields that same UTC instant, and
// the UTC getters read it back unshifted), but that round-trip depends on
// the JS engine's ISO-8601 parser behaving exactly this way. Extracting the
// digits straight out of the string with a regex removes that dependency
// entirely — there is then no `Date` arithmetic left that could ever
// introduce a timezone shift, regardless of the runtime's own clock/locale.
import { isUnknown, type Unknownable } from "@/contracts/display";

const MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
] as const;

// Matches exactly what toIsoFromZohoDate produces: `YYYY-MM-DDTHH:mm:ssZ`.
const ISO_TIMESTAMP_PATTERN =
  /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})Z$/;

/**
 * Formats an ISO 8601 timestamp string (as produced by `toIsoFromZohoDate`)
 * as "07-Aug-2026 12:06:37". Parsed directly off the string's digits — never
 * through a `Date` object — so no timezone conversion of any kind can occur;
 * the digits shown are always exactly the digits recorded. A value that
 * doesn't match the expected shape is returned unchanged — never fabricate a
 * "corrected" value for something we can't confidently read (ADR-004 D4's
 * "never faked" applies here just as much as to a missing field).
 */
export function formatTimestamp(raw: string): string {
  const match = ISO_TIMESTAMP_PATTERN.exec(raw);
  if (!match) {
    return raw;
  }
  const [, year, month, day, hh, mm, ss] = match;
  const monthIndex = Number(month) - 1;
  const monthName = MONTHS[monthIndex];
  if (monthName === undefined) {
    return raw;
  }
  return `${day}-${monthName}-${year} ${hh}:${mm}:${ss}`;
}

/**
 * `formatTimestamp`'s `Unknownable`-aware counterpart, for timestamp fields
 * rendered straight from a display contract — passes the `unknown` sentinel
 * through untouched so `Field`/callers keep their existing "unknown"
 * handling unchanged.
 */
export function formatUnknownableTimestamp(value: Unknownable<string>): Unknownable<string> {
  return isUnknown(value) ? value : formatTimestamp(value);
}
